import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { NegocioService } from '../../services/negocio.service';
import { ToastService } from '../../services/toast.service';
import { Visita, VisitasService } from '../../services/visitas.service';
import {
  ASISTENCIA_ACTIVIDAD_GRUPAL_ACCEPT,
  validateAsistenciaActividadGrupalFile,
} from '../../utils/asistencia-actividad-grupal-upload-constraints';
import {
  extraerAdjuntoResumenFirmadoDesdeVisita,
  visitaTieneDocumentoResumenFirmado,
  type VisitaResumenFirmadoAdjunto,
} from '../../utils/visita-resumen-firmado-adjunto.util';
import { abrirODescargarAdjunto } from '../../utils/abrir-adjunto.util';

@Component({
  selector: 'app-formulario-resumen-firmado-visita',
  templateUrl: './formulario-resumen-firmado-visita.component.html',
  styleUrls: ['./formulario-resumen-firmado-visita.component.scss'],
  standalone: false,
})
export class FormularioResumenFirmadoVisitaComponent implements OnChanges {
  @Input() visible = false;
  @Input() dentroDeMenu = false;
  @Input() visita: Visita | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() guardado = new EventEmitter<void>();

  readonly fileAccept = ASISTENCIA_ACTIVIDAD_GRUPAL_ACCEPT;

  archivoSeleccionado: File | null = null;
  nombreArchivoMostrar = '';
  subiendo = false;
  eliminando = false;
  adjuntoExistente: VisitaResumenFirmadoAdjunto | null = null;
  teniaArchivoAlAbrir = false;

  private readonly visitasService = inject(VisitasService);
  private readonly negocioService = inject(NegocioService);
  private readonly toastService = inject(ToastService);
  private readonly alertController = inject(AlertController);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visita']) {
      this.adjuntoExistente = extraerAdjuntoResumenFirmadoDesdeVisita(this.visita);
      if (changes['visita'].firstChange || !changes['visita'].previousValue) {
        this.teniaArchivoAlAbrir = visitaTieneDocumentoResumenFirmado(this.visita);
      }
    }
  }

  get textoBotonPrincipal(): string {
    return this.teniaArchivoAlAbrir ? 'Guardar Cambios' : 'Subir Archivo';
  }

  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetArchivo();
  }

  onArchivoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.resetArchivo();
      return;
    }
    const check = validateAsistenciaActividadGrupalFile(file);
    if (!check.valid) {
      void this.toastService.mostrar(check.message, 'warning');
      input.value = '';
      this.resetArchivo();
      return;
    }
    this.archivoSeleccionado = file;
    this.nombreArchivoMostrar = file.name;
  }

  activarSelectorArchivo(input: HTMLInputElement): void {
    if (this.subiendo || this.eliminando) return;
    input.click();
  }

  verArchivoExistente(): void {
    if (!this.adjuntoExistente || this.subiendo || this.eliminando) return;
    abrirODescargarAdjunto(
      this.adjuntoExistente,
      this.negocioService,
      this.visitasService,
      this.toastService
    );
  }

  eliminarArchivoExistente(): void {
    if (!this.adjuntoExistente || this.subiendo || this.eliminando) return;
    void this.confirmarYEliminar();
  }

  onSubirClick(): void {
    void this.subir();
  }

  private async confirmarYEliminar(): Promise<void> {
    const id = this.visita?.id_visita;
    if (!id) {
      await this.toastService.mostrar('No se pudo identificar la visita.', 'error');
      return;
    }

    const alert = await this.alertController.create({
      header: 'Eliminar resumen firmado',
      message:
        '¿Está seguro de que desea eliminar el resumen firmado? Esta acción no se puede deshacer.',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Sí, eliminar', role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') return;

    this.eliminando = true;
    try {
      await firstValueFrom(this.visitasService.eliminarResumenVisita(id));
      await this.toastService.mostrar('Resumen firmado eliminado correctamente', 'success');
      this.adjuntoExistente = null;
      this.teniaArchivoAlAbrir = false;
      this.guardado.emit();
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } | string };
      const mensaje =
        (typeof err?.error === 'object' && err?.error?.message) ||
        (typeof err?.error === 'string' ? err.error : null) ||
        'Error al eliminar el resumen firmado';
      await this.toastService.mostrar(mensaje, 'error');
    } finally {
      this.eliminando = false;
    }
  }

  private async subir(): Promise<void> {
    if (this.subiendo || !this.archivoSeleccionado) {
      if (!this.archivoSeleccionado) {
        await this.toastService.mostrar('Seleccione un archivo de resumen firmado.', 'warning');
      }
      return;
    }

    const id = this.visita?.id_visita;
    if (!id) {
      await this.toastService.mostrar('No se pudo identificar la visita.', 'error');
      return;
    }

    if (this.adjuntoExistente != null) {
      const confirmado = await this.confirmarReemplazo();
      if (!confirmado) return;
    }

    this.subiendo = true;
    try {
      await firstValueFrom(
        this.visitasService.guardarResumenVisita(id, this.archivoSeleccionado)
      );
      await this.toastService.mostrar('Resumen firmado subido correctamente', 'success');
      this.guardado.emit();
      this.cerrar();
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } | string };
      const mensaje =
        (typeof err?.error === 'object' && err?.error?.message) ||
        (typeof err?.error === 'string' ? err.error : null) ||
        'Error al subir el resumen firmado';
      await this.toastService.mostrar(mensaje, 'error');
    } finally {
      this.subiendo = false;
    }
  }

  private async confirmarReemplazo(): Promise<boolean> {
    const alert = await this.alertController.create({
      header: 'Reemplazar resumen firmado',
      message:
        'Ya existe un resumen firmado para esta visita. Si continúa, el documento anterior se eliminará de forma irreversible y será reemplazado por el nuevo archivo.',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Sí, reemplazar', role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  private resetArchivo(): void {
    this.archivoSeleccionado = null;
    this.nombreArchivoMostrar = '';
  }
}
